import React, { useEffect, useState } from "react";
import { List, ListItem, Link, CircularProgress, Button } from "@mui/material";
if (typeof window !== "undefined") {
	// Only import the XMLParser on browser
	var XMLParser = require("react-xml-parser");
}

const BACKEND_URL = process.env.GATSBY_BACKEND_URL;

function Orcid({ id }) {
	const [posts, setPosts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [orcidError, setOrcidError] = useState(false);

	useEffect(() => {
		getOrcidData(id);
	}, []);

	const getOrcidData = async (id) => {
		setLoading(true);
		let response = await fetch(`${BACKEND_URL}/orcid/${id}?_format=json`);

		if (response.ok) {
			let data = await response.json();
			if (
				data &&
				!!data.data &&
				typeof data.data["activities-summary"] !== "undefined"
			) {
				// Access inside ORCID object and targeting lasts 5 works
				let posts = data.data["activities-summary"].works.group
					.reverse()
					.slice(-5);
				posts.reverse();

				if (posts.length === 0) {
					setOrcidError(true);
					setLoading(false);
					return;
				}

				// Create an array of promises to fetch data for each work
				let workPromises = posts.map((post) => {
					let putCode = post["work-summary"][0]["put-code"];
					return fetch(`https://pub.orcid.org/v3.0/${id}/work/${putCode}`)
						.then((response) => {
							if (!response.ok) {
								throw new Error("Network response was not ok");
							}
							return response.text();
						})
						.then((text) => {
							let xmlData = new XMLParser().parseFromString(text);
							return xmlData;
						})
						.catch((error) => {
							console.error(error);
							return null;
						});
				});

				// Wait for all the promises to resolve and get the data for each work
				Promise.all(workPromises)
					.then((workData) => {
						// Add the fetched data to the posts array
						let updatedPosts = posts.map((post, index) => {
							return {
								...post,
								data: workData[index],
							};
						});
						setPosts(updatedPosts);
						setLoading(false);
					})
					.catch((error) => {
						console.error(error);
						setLoading(false);
					});
			} else {
				let posts = data;
				setPosts(posts);
				setLoading(false);
			}
		}
	};

	if (loading) return <CircularProgress />;

	return (
		<>
			<List
				sx={{
					width: "100%",
					display: { xxs: "block", md: "flex" },
					flexWrap: "wrap",
				}}
			>
				{orcidError && (
					<p>
						We couldn't find any posts. This could be because the ORCID account
						owner has it set to private mode, or there are no posts uploaded.
					</p>
				)}

				{Array.isArray(posts) && posts.length === 0 && (
					<p>Your ORCID ID is not valid. Can't resolve the query.</p>
				)}

				{Array.isArray(posts) &&
					posts.length > 0 &&
					posts.map((post) => {
						const { data, "work-summary": workSummary } = post || {};
						const lastIndex = data?.children?.length - 1;
						const { children } = data?.children?.[lastIndex] || {};
						const {
							title,
							"journal-title": journalTitle,
							"publication-date": publicationDate,
							"external-ids": externalIds,
							url,
						} = workSummary?.[0] || {};

						const titleValue = title?.title?.value || "";
						const journalTitleValue = journalTitle?.value || "";
						const publicationDateValue =
							publicationDate &&
							[
								publicationDate.day?.value,
								publicationDate.month?.value,
								publicationDate.year?.value,
							]
								.filter((datePart) => datePart)
								.join("/");
						const externalIdValue =
							externalIds?.["external-id"]?.[0]?.["external-id-value"] || "";
						const urlValue = url?.value || "#";

						return (
							<ListItem style={{ padding: "15px 0" }}>
								<Link
									underline="hover"
									style={{ color: "#000" }}
									href={urlValue}
									target="_blank"
									rel="noreferrer noopener"
								>
									{[
										children
											?.map((child) => child?.children?.[0]?.value)
											.filter((value) => value)
											.join(", ") || "",
										titleValue,
										journalTitleValue,
										publicationDateValue,
										externalIdValue,
									]
										.filter((segment) => segment)
										.join(" - ")}
								</Link>
							</ListItem>
						);
					})}
			</List>
			<Button variant="outlined">
				<Link
					underline="none"
					href={`https://orcid.org/${id}`}
					target="_blank"
					rel="noreferrer noopener"
				>
					View ORCID Profile
				</Link>
			</Button>
		</>
	);
}

export default Orcid;
